/**
 * PII Scrubbing Utilities (GDPR Art.32 Compliance)
 *
 * Privacy-preserving utilities for scrubbing personally identifiable information
 * from error events before sending to Sentry.
 *
 * Extracted from analytics.common.ts (2026-01-07) - Week 3-4 Complex Initiatives
 * M4.1 (2026-02-08): Added event.contexts scrubbing, event.user IP scrubbing,
 *                     SAFE_FIELDS allowlist for observability identifiers
 */

/**
 * PII patterns to scrub from error events
 * Protects user privacy - matches Edge Functions implementation
 *
 * Pattern Order (2026-01-07 Code Review Fix):
 * - Most specific patterns first (creditCard, ssn, jwt)
 * - Generic patterns last (phone) to prevent greedy matching
 *
 * @see supabase/functions/_shared/utils/sentry-edge.ts
 */
export const PII_PATTERNS = {
  // Most specific patterns first
  creditCard: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
  ssn: /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g,
  jwt: /eyJ[a-zA-Z0-9_-]+\.eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+/g,
  email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  uuid: /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
  ipv4: /\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\b/g,
  ipv6: /\b(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}\b|\b(?:[0-9a-fA-F]{1,4}:){1,7}:|\b::(?:[0-9a-fA-F]{1,4}:){0,5}[0-9a-fA-F]{1,4}\b/g,
  // International + domestic phone: +CC (area) local-parts
  // Covers: +1 555-123-4567, (555) 123-4567, +44 20 7946 0958, +81 3-1234-5678
  phone: /(?:\+[1-9]\d{0,2}[-.\s]?)?(?:\(?\d{1,4}\)?[-.\s]?){2,}\d{2,4}\b/g,
};

/**
 * Performance optimization (2026-01-07): Module-level constants prevent recreation
 * in hot path (scrubPII called in every Sentry beforeSend hook)
 */
const ALLOWED_HEADERS = new Set([
  'content-type',
  'accept',
  'accept-language',
  'accept-encoding',
  'user-agent',
  'content-length',
  'cache-control',
  'origin',
  'referer', // Useful for debugging, contains page URL only
]);

/**
 * M4.1: Safe fields that should NEVER be redacted (observability/tracing)
 *
 * System-generated identifiers essential for debugging and distributed tracing.
 * NOT PII per GDPR Art.4(1) — they don't identify natural persons.
 * Matches edge-side SAFE_FIELDS in sentry-edge/config.ts
 */
const SAFE_FIELDS = new Set([
  'request_id',
  'trace_id',
  'span_id',
  'transaction_id',
  'correlation_id',
  'session_id', // System session, not user session
  'attempt_number',
  'function_name',
  'provider',
  'model',
  'error_type',
  'circuit_state',
  // Observability fields that contain sensitive substrings but are NOT PII
  'user_id',          // Contains 'user' but is a system identifier
  'device_id',        // Contains 'device' but is a system identifier
  'device_type',      // Contains 'device' but is metadata
  'message_count',    // Contains 'message' but is a counter
  'message_id',       // Contains 'message' but is a system identifier
  'token_count',      // Contains 'token' but is a counter
  'token_usage',      // Contains 'token' but is metrics
]);

/**
 * DD-442: Unified sensitive key detection (substring matching)
 *
 * Replaces both the old SENSITIVE_KEY_PATTERN regex (query string scrubbing)
 * and inline lowerKey.includes() checks (object scrubbing).
 *
 * Substring matching catches compound keys:
 *   - "user_message" matches → redacted (user drama messages)
 *   - "access_token" matches → redacted (auth tokens)
 *   - "client_secret" matches → redacted (credentials)
 *   - "system_prompt" matches → redacted (AI prompt content)
 *
 * SAFE_FIELDS allowlist (checked first) protects observability identifiers
 * like "request_id", "trace_id", "function_name" from false-positive redaction.
 */
const SENSITIVE_KEYS = [
  'token', 'secret', 'key', 'email', 'auth', 'bearer', 'password',
  'session', 'cookie', 'authorization', 'api_key', 'apikey', 'device',
  'message', 'prompt',
];

/**
 * DD-442: Check if a key name refers to sensitive data.
 * Used by both scrubPII (query params) and scrubObject (object keys).
 *
 * Checks SAFE_FIELDS first (allowlist takes precedence), then
 * uses word-boundary regex to detect sensitive key names.
 */
function isSensitiveKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  if (SAFE_FIELDS.has(key) || SAFE_FIELDS.has(lowerKey)) {
    return false;
  }
  return SENSITIVE_KEYS.some((k) => lowerKey.includes(k));
}

/**
 * Scrub PII from a string
 *
 * Type Safety (2026-01-07 Code Review Fix):
 * - Accepts any type to match defensive implementation
 * - Returns non-string inputs unchanged (defensive fail-safe)
 * - Returns scrubbed string for string inputs
 */
export function scrubString(str: string): string;
export function scrubString(str: any): any;
export function scrubString(str: any): any {
  if (typeof str !== 'string') return str;

  let scrubbed = str;
  for (const [type, pattern] of Object.entries(PII_PATTERNS)) {
    scrubbed = scrubbed.replace(pattern, `[REDACTED_${type.toUpperCase()}]`);
  }
  return scrubbed;
}

/**
 * Scrub PII from an object (deep)
 *
 * Safety guards (2026-01-07 security fix):
 * - MAX_DEPTH prevents stack overflow from deeply nested objects
 * - visited WeakSet prevents infinite recursion from circular references
 *
 * @param obj - Object to scrub
 * @param depth - Current recursion depth (internal)
 * @param visited - Set of visited objects for circular reference detection (internal)
 */
export function scrubObject(obj: any, depth = 0, visited = new WeakSet()): any {
  const MAX_DEPTH = 20; // Consensus recommendation: 10-20

  // Depth limit guard (prevents stack overflow)
  if (depth > MAX_DEPTH) {
    return '[REDACTED_DEEP_OBJECT]';
  }

  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'string') return scrubString(obj);

  // Circular reference guard (prevents infinite recursion)
  if (typeof obj === 'object') {
    if (visited.has(obj)) {
      return '[REDACTED_CIRCULAR]';
    }
    visited.add(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => scrubObject(item, depth + 1, visited));
  }

  if (typeof obj === 'object') {
    const scrubbed: any = {};
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();

      // DD-442: Unified sensitive key detection (SAFE_FIELDS checked inside isSensitiveKey)
      if (SAFE_FIELDS.has(key) || SAFE_FIELDS.has(lowerKey)) {
        scrubbed[key] = value;
        continue;
      }

      // Completely redact sensitive keys (word-boundary matching via isSensitiveKey)
      if (isSensitiveKey(key)) {
        scrubbed[key] = '[REDACTED]';
      } else {
        scrubbed[key] = scrubObject(value, depth + 1, visited);
      }
    }
    return scrubbed;
  }

  return obj;
}

/**
 * Scrub PII from Sentry event
 * Used in beforeSend hook for GDPR compliance
 *
 * M4.1: Added event.user IP scrubbing (parity with edge-side)
 * M4.1: Added event.contexts scrubbing (setContext() data was unfiltered)
 */
export function scrubPII(event: any): any {
  // M4.1: Scrub user IP addresses (GDPR Art.4(1) - PII)
  // Parity with edge-side pii-scrubbing.ts
  if (event.user) {
    const scrubbedUser: any = {};
    for (const [key, value] of Object.entries(event.user)) {
      if (key.toLowerCase() === 'ip' || key.toLowerCase() === 'ip_address') {
        scrubbedUser[key] = '[REDACTED_IP]';
      } else if (typeof value === 'string') {
        scrubbedUser[key] = scrubString(value);
      } else {
        scrubbedUser[key] = scrubObject(value);
      }
    }
    event.user = scrubbedUser;
  }

  // Scrub request body (user messages, prompts)
  if (event.request?.data) {
    event.request.data = '[REDACTED_REQUEST_BODY]';
  }

  // Security: Deny-by-default header scrubbing (GDPR Art.32, PCI DSS compliant)
  // Only allow safe, non-identifying headers (using module-level constant)
  if (event.request?.headers) {
    const scrubbedHeaders: Record<string, string> = {};
    for (const [key, value] of Object.entries(event.request.headers)) {
      const lowerKey = key.toLowerCase();
      if (ALLOWED_HEADERS.has(lowerKey)) {
        // Scrub PII from allowed headers too
        scrubbedHeaders[key] = typeof value === 'string' ? scrubString(value) : '[REDACTED]';
      } else {
        scrubbedHeaders[key] = '[REDACTED]';
      }
    }
    event.request.headers = scrubbedHeaders;
  }

  // Security: Key-based query string scrubbing (handles URL encoding, using module-level pattern)
  if (event.request?.query_string) {
    try {
      const params = new URLSearchParams(event.request.query_string);

      const scrubbedParams = new URLSearchParams();
      for (const [key, value] of params.entries()) {
        if (isSensitiveKey(key)) {
          scrubbedParams.set(key, '[REDACTED]');
        } else {
          scrubbedParams.set(key, scrubString(value));
        }
      }
      event.request.query_string = scrubbedParams.toString();
    } catch {
      // Malformed query string - redact entirely
      event.request.query_string = '[REDACTED]';
    }
  }

  // Scrub exception messages
  if (event.exception?.values) {
    for (const exception of event.exception.values) {
      if (exception.value) {
        exception.value = scrubString(exception.value);
      }
    }
  }

  // Scrub extra context
  if (event.extra) {
    event.extra = scrubObject(event.extra);
  }

  // Scrub tags
  if (event.tags) {
    event.tags = scrubObject(event.tags);
  }

  // M4.1: Scrub contexts set via Sentry.setContext()
  // These can contain arbitrary data from setAnalysisContext(), setDeviceContext(), etc.
  if (event.contexts) {
    const scrubbedContexts: any = {};
    for (const [contextName, contextData] of Object.entries(event.contexts)) {
      // Preserve Sentry's built-in contexts (os, browser, device, runtime, trace)
      // which are auto-populated by the SDK and contain no user PII
      if (['os', 'browser', 'device', 'runtime', 'trace', 'app', 'culture'].includes(contextName)) {
        scrubbedContexts[contextName] = contextData;
      } else {
        scrubbedContexts[contextName] = scrubObject(contextData);
      }
    }
    event.contexts = scrubbedContexts;
  }

  return event;
}

/**
 * M4.2: Scrub error properties before sending to PostHog
 *
 * PostHog has NO beforeSend hook (unlike Sentry), so PII scrubbing must happen
 * at call sites. This utility scrubs the three common PII vectors in error events:
 * 1. error_message — may contain user input echoed back in error text
 * 2. error_stack — may contain user data in stack frame arguments
 * 3. context spread — arbitrary caller-provided context may contain PII
 *
 * Safe properties (sentry_event_id, request_id, etc.) are preserved.
 *
 * @param errorMessage - Error.message to scrub
 * @param errorStack - Error.stack to scrub and truncate
 * @param context - Arbitrary context object to deep-scrub
 * @returns Scrubbed properties safe for PostHog capture
 */
export function scrubErrorForPostHog(
  errorMessage: string,
  errorStack?: string,
  context?: Record<string, any>,
): {
  error_message: string;
  error_stack?: string;
  scrubbedContext: Record<string, any>;
} {
  const MAX_STACK_LENGTH = 500; // PostHog doesn't need full stack traces

  return {
    error_message: scrubString(errorMessage) || '[unknown]',
    error_stack: errorStack
      ? scrubString(errorStack.slice(0, MAX_STACK_LENGTH))
      : undefined,
    scrubbedContext: context ? scrubObject(context) : {},
  };
}

/**
 * Scrub PII from breadcrumb
 * Used in beforeBreadcrumb hook
 */
export function scrubBreadcrumb(breadcrumb: any): any {
  if (breadcrumb.message) {
    breadcrumb.message = scrubString(breadcrumb.message);
  }
  if (breadcrumb.data) {
    breadcrumb.data = scrubObject(breadcrumb.data);
  }
  return breadcrumb;
}
